import React from 'react';
import styled from 'styled-components';
import {FormField} from '../utils/use-form-field';
import {useComponent} from '../component-library';
import {useFeatureAppId} from '../utils/use-feature-app-id';
import {SelectOptionGroup, SelectOption} from '../types';
import {generateTestId, TestIdElementType} from '../utils/test';
import {SelectConsolidated} from './select-consolidated';
import {useDevConfig} from '../utils/use-dev-config';
import {UserDrivingProfile} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

export interface ModelSelectionProps {
  modelHeadline?: string;
  modelTypeSelectGroups: SelectOptionGroup[];
  modelTypeField: FormField<string>;
  modelEngineSelectOptions: SelectOption[];
  modelEngineField: FormField<string>;
  setSelectedUserDrivingProfile?: (
    selectedUserDrivingProfile: UserDrivingProfile | undefined
  ) => void;
}

export const ModelSelection = ({
  modelHeadline,
  modelTypeField,
  modelTypeSelectGroups,
  modelEngineField,
  modelEngineSelectOptions,
  setSelectedUserDrivingProfile,
}: ModelSelectionProps): JSX.Element => {
  const {modelCombinedDropdowns} = useDevConfig();
  const featureAppId = useFeatureAppId();
  const Text = useComponent('Text');
  const Select = useComponent('Select');
  const GroupedSelect = useComponent('GroupedSelect');

  const createHandleOnChangeSelect =
    (field: FormField<any>) => (newValue: any) => {
      if (setSelectedUserDrivingProfile) {
        setSelectedUserDrivingProfile(undefined);
      }
      field.onChange(newValue);
    };

  return (
    <>
      {/*
        Defaulting to non-breaking spaces so that the height doesn't collapse
        when the text is missing while loading models. For some reason, normal
        spaces collapse too.
      */}
      <HeaderWrapper>
        <Text
          bold
          tag="h3"
          variant="copy"
          testid={generateTestId({
            featureAppId,
            elementName: 'vehicle-section-headline',
            elementType: TestIdElementType.dynamic,
          })}
        >
          {modelHeadline || '\u00A0'}
        </Text>
      </HeaderWrapper>

      <ModelSelectWrapper>
        {modelCombinedDropdowns ? (
          <SelectConsolidated
            label={`${modelTypeField.label} / ${modelEngineField.label}`}
            formFieldA={modelTypeField}
            formFieldB={modelEngineField}
            combinedSelectOptions={modelTypeSelectGroups}
            testId={generateTestId({
              featureAppId,
              elementName: 'model-engine-combined',
              elementType: TestIdElementType.dynamic,
            })}
            setSelectedUserDrivingProfile={setSelectedUserDrivingProfile}
          />
        ) : (
          <ModelSelectsGrid>
            <ModelTypeDropdownWrapper>
              <GroupedSelect
                label={modelTypeField.label}
                selectedValue={modelTypeField.value}
                onChange={createHandleOnChangeSelect(modelTypeField)}
                optionGroups={modelTypeSelectGroups}
                testid={generateTestId({
                  featureAppId,
                  elementName: 'vehicle-section-model',
                  elementType: TestIdElementType.select,
                })}
              />
            </ModelTypeDropdownWrapper>
            {/*
          It's possible that we have 0 engines, because they were filtered out
          even though we have a model. In this case the select is not
          displayed.
        */}
            {modelEngineSelectOptions.length > 0 && (
              <ModelEngineDropdownWrapper>
                <Select
                  label={modelEngineField.label}
                  selectedValue={modelEngineField.value}
                  onChange={createHandleOnChangeSelect(modelEngineField)}
                  options={modelEngineSelectOptions}
                  testid={generateTestId({
                    featureAppId,
                    elementName: 'vehicle-section-engine',
                    elementType: TestIdElementType.select,
                  })}
                />
              </ModelEngineDropdownWrapper>
            )}
          </ModelSelectsGrid>
        )}
      </ModelSelectWrapper>
    </>
  );
};

const ModelSelectWrapper = styled.div`
  padding-top: ${(props) => props.theme.internal.size.static200};
`;

const ModelSelectsGrid = styled.div`
  display: grid;
  grid-template: ' modeltype . modelmotor ' / 3fr 1fr 3fr;
`;

const HeaderWrapper = styled.div`
  margin-top: ${(props) =>
    props.theme.brand.elements.modelSelectionArea?.header.marginTop || 0};
`;

const ModelTypeDropdownWrapper = styled.div`
  grid-area: modeltype;
`;

const ModelEngineDropdownWrapper = styled.div`
  grid-area: modelmotor;
`;
